import { BaseEntity, BaseSearchForm } from 'entities'
import { Background } from './background'
import { Coupon } from './coupon'
import { CouponCode } from './coupon_code'
import { DisposalHistory } from './disposal_history'
import { Institution } from './institution'
import { Patient, PatientForm } from './patient'
import { ProductItem } from './product_item'
import { Rack } from './rack'
import { RequestForm } from './request_form'
import { Result } from './result'
import { TransportationReport } from './transportation_report'
import { User } from './user'

export type SmokingStatus = 'NEVER' | 'BEFORE' | 'SMOKING'
export type QuantityStatus = 'OK' | 'REFERENCE' | 'NG'
export type ResultStatus = 'OK' | 'REFERENCE' | 'NG'

export type Sample = BaseEntity & {
    code?: string
    tubeLocation?: string
    inspectionDate?: string
    invoiceDate?: string
    collectionDate?: string
    collectionTime?: string
    deliveryDate?: string
    deadlineDate?: string
    smokingStatus?: SmokingStatus
    smokingPerDay?: number
    smokingYears?: number
    smokingStartAge?: number
    smokingEndAge?: number
    alcoholPerWeek?: number
    menstruation?: boolean
    pregnancy?: boolean
    karteNumber?: string
    extractionCount?: number
    note?: string
    patient?: Patient
    patientId?: number
    institution?: Institution
    institutionId?: number
    rack?: Rack
    rackId?: number
    transportationReport?: TransportationReport
    transportationReportId?: number
    freezerLocation?: string
    productItems?: ProductItem[]
    status?: SampleStatus
    inspectionStatus?: InspectionStatus
    familyHistories?: FamilyHistory[]
    pastMedicalHistories?: PastMedicalHistory[]
    currentMedicalHistories?: CurrentMedicalHistory[]
    doctorName?: string
    disposalHistory?: DisposalHistory
    disposedDate?: string
    version?: number
    sendDate?: string
    quantity?: number
    isRecollect?: boolean

    receiptUser?: User
    inputtedUser?: User
    confirmedUser?: User
    resultCheckedUser?: User
    isResultChecked?: boolean
    isPrintResultChecked?: boolean
    resultNote?: string
    trackNumber?: string

    nonUrine?: boolean
    differentContainer?: boolean
    otherAbnormalities?: boolean

    temperature?: number
    overTemperature?: boolean
    isChilledTransport?: boolean
    includeBlood?: boolean
    followupStudyAgreement?: boolean

    quantityStatus?: QuantityStatus
    resultStatus?: ResultStatus
    coupons?: Coupon[]
    couponCodes?: CouponCode[]
    requestForm?: RequestForm
    results?: Result[]

    currentMedicalHistoryNote?: string
    pastMedicalHistoryNote?: string
    familyHistoryNote?: string
    smokingNote?: string
    alcoholNote?: string

    background?: Background
    isTest?: boolean
    price?: number
    discount?: number
    sendHistoryId?: number
    pcrRunIds?: number[]
    priority?: number
    resultPdf?: string | File
    productSetId?: number
    productNames?: string
    inspectionMethod?: 'NGS' | 'PCR'
}

export type SampleForm = Sample & {
    institutionId?: number
    freezerLocation?: string
    tubeLocation?: string
    transportationReportId?: number
    requestFormBarcode?: string
    tubeBarcode?: string
    checkedPatient?: boolean
    checkedResultPdf?: boolean
    hasNoProblem?: boolean
}

export type SampleInputForm = Sample & {
    patient?: PatientForm
    readName?: boolean
    checkedName?: boolean
    readGender?: boolean
    checkedGender?: boolean
    readBirthday?: boolean
    checkedBirthday?: boolean
    readAddress?: boolean
    checkedAddress?: boolean
    readEmail?: boolean
    checkedEmail?: boolean
    readTel?: boolean
    checkedTel?: boolean
    readLocation?: boolean
    checkedLocation?: boolean
    readProduct?: boolean
    checkedProduct?: boolean
    readCollectionDate?: boolean
    checkedCollectionDate?: boolean
    readKarte?: boolean
    checkedKarte?: boolean
    readCurrentMedicalHistories?: boolean
    checkedCurrentMedicalHistories?: boolean
    readPastMedicalHistories?: boolean
    checkedPastMedicalHistories?: boolean
    readFamilyHistories?: boolean
    checkedFamilyHistories?: boolean
    readAlcohol?: boolean
    checkedAlcohol?: boolean
    readSmoking?: boolean
    checkedSmoking?: boolean
    readPregnancy?: boolean
    checkedPregnancy?: boolean
    readMenstruation?: boolean
    checkedMenstruation?: boolean
    readCoupons?: boolean
    checkedCoupons?: boolean
    readInstitution?: boolean
    checkedInstitution?: boolean
    readDoctor?: boolean
    checkedDoctor?: boolean

    checkedCurrentMedicalHistoryNote?: boolean
    readCurrentMedicalHistoryNote?: boolean
    checkedPastMedicalHistoryNote?: boolean
    readPastMedicalHistoryNote?: boolean
    checkedFamilyHistoryNote?: boolean
    readFamilyHistoryNote?: boolean
    checkedSmokingNote?: boolean
    readSmokingNote?: boolean
    checkedAlcoholNote?: boolean
    readAlcoholNote?: boolean
    checkedFollowupStudyAgreement?: boolean
    readFollowupStudyAgreement?: boolean

    smokingStatus?: SmokingStatus
    smokingPerDay?: number
    smokingYears?: number
    smokingStartAge?: number
    smokingEndAge?: number
    alcoholPerWeek?: number
    currentCancerHistories?: CurrentMedicalHistory[]
    pastCancerHistories?: CurrentMedicalHistory[]
    familyHistories?: FamilyHistory[]

    requestFormBarcode?: string
    preRequestFormBarcode?: string
    tubeBarcode?: string
    productItemIds?: number[]
    hasCoupon?: boolean
    couponIds?: (number | undefined)[]
    couponCodes?: string[] | CouponCode[]

    agreement?: boolean
    followupStudyAgreement?: boolean
    background?: Background
}

export type BulkSampleForm = Array<SampleForm | undefined>

export type SampleSearchForm = BaseSearchForm & {
    pjPatientId?: string | null
    rackId?: string | null
    tubeId?: string | null
    sampleIdIn?: string
    unusedSample?: boolean
    patientIdIn?: string
    code?: string
    requestForm?: string
    sampleStatus?: SampleStatus
    inspectionStatus?: InspectionStatus
    inspectionDateAfter?: string
    inspectionDateBefore?: string
    createdAtAfter?: string
    createdAtBefore?: string
    institutionId?: number
    sendHistoryId?: number
    send_date_isnull?: boolean
    sendDateAfter?: string
    sendDateBefore?: string
    deadlineDateAfter?: string
    deadlineDateBefore?: string
    deliveryDateAfter?: string
    deliveryDateBefore?: string
    disposedDateAfter?: string
    disposedDateBefore?: string
    isStandard?: boolean
    resultStatus?: string
    withResults?: boolean
    withPureResults?: boolean
    withTransportationReports?: boolean
    listSerializer?: boolean
    salesAgencyId?: number
    transportationReportIdIn?: string
    trackNumber?: string
    inspectionMethod?: 'NGS' | 'PCR'
    institutionIsSpecialResult?: boolean
    request_form__is_result_confirm_required?: boolean
}

export const sampleFormParams = [{ attr: 'staff', type: 'select', selectItems: [] }]

export const SAMPLE_STATUS = [
    'INTERVIEWED',
    'WAREHOUSED',
    'NGS_IN_PROGRESS',
    'RNA_EXTRACTED',
    'DNA_SYNTHESIZED',
    'PCR_READY',
    'COMPLETED',
    'PRINT_REQUESTED',
    'SENT',
    'DISPOSED',
    'RECOLLECT',
]

export type SampleStatus = (typeof SAMPLE_STATUS)[number]
export const SAMPLE_STATUS_JP = [
    '問診票入力済',
    '検査待ち',
    'NGS測定中',
    '抽出・精製済',
    'cDNA合成済',
    'qPCR準備中',
    '検査済',
    '印刷依頼済',
    '送付済',
    '廃棄済',
    '再採尿',
]

export const SAMPLE_STATUS_SELECT_ITEMS = SAMPLE_STATUS.map((value, index) => {
    return { label: SAMPLE_STATUS_JP[index], value: value }
})

export const INSPECTION_STATUS = ['DRAFT', 'INPUTTED', 'INSPECTED']
export type InspectionStatus = (typeof INSPECTION_STATUS)[number]
export const INSPECTION_STATUS_JP = ['未入力', '入力済', '確認済']
export const INSPECTION_STATUS_SELECT_ITEMS = INSPECTION_STATUS.map((value, index) => {
    return { label: INSPECTION_STATUS_JP[index], value: value }
})

export const QUANTITY_STATUS = ['OK', 'NG']
export const QUANTITY_STATUS_JP = ['規定量以上の尿量', '1ml以下のため検査不可']
export const QUANTITY_STATUS_SELECT_ITEMS = QUANTITY_STATUS.map((value, index) => {
    return { label: QUANTITY_STATUS_JP[index], value: value }
})

export const RESULT_STATUS = ['OK', 'REFERENCE', 'NG']
export const RESULT_STATUS_JP = ['正常', '参考値', '検査不可']
export const RESULT_STATUS_SELECT_ITEMS = RESULT_STATUS.map((value, index) => {
    return { label: RESULT_STATUS_JP[index], value: value }
})

export type FamilyHistory = {
    cancer?: string
    relationship?: string
    withinFirstDegreeRelative?: number
    secondDegreeOrHigher?: number
}

export type MedicalHistory = {
    disease?: string
    part?: string
}

export type CurrentMedicalHistory = MedicalHistory
export type PastMedicalHistory = MedicalHistory

export const SMOKING_STATUS_SELECT_ITEMS = [
    { label: '吸わない', value: 'NEVER' },
    { label: '吸っていた', value: 'BEFORE' },
    { label: '吸う', value: 'SMOKING' },
]
